<template>
  <div class="bank-info-rent-index">
    <!-- Add Bank Account Modal -->
    <hl-Modal
      v-if="isEditTenancyBankAccountModalShown"
      class="text-center"
      iconClass="budicon-withdraw-money text-primary"
      title="Bruk en annen bankkonto"
    >
      <p>
        {{ translationChooseBankRentPayment }}
        <router-link :to="{ name: 'landlord_profile_default_settings', params: { landlordId: this.landlordId } }">{{
          translationRetrieveAccountNumber
        }}</router-link>
      </p>

      <hl-BankAccountSelect
        :bankAccounts="bankAccounts"
        :defaultOption="currentBankAccount"
        @input="setNewBankAccount($event)"
      />

      <div class="mt-5">
        <button class="btn btn-primary mx-2" @click="changeCurrentBankAccount">{{ translationUse }}</button>
        <button class="btn btn-outline-secondary mx-2" @click="toggleEditTenancyBankAccountModal">
          {{ translationCancel }}
        </button>
      </div>
    </hl-Modal>
    <!-- Add Bank Account Modal -->
    <BlockContainer class="p-4 flex-fill h-100">
      <div class="row">
        <div class="col">
          <h5>{{ translationBankAccount }}</h5>
        </div>

        <div v-if="shouldShowEditButton || isUserSudo" class="col text-right">
          <EditButton
            @click.native="toggleEditTenancyBankAccountModal"
            :class="{ sudo: !shouldShowEditButton && isUserSudo }"
          />
        </div>
      </div>

      <!-- If tenancy has no bank account and the landlord doesn't have any bank account -->
      <div v-if="tenancyHasNoBankAccount && !landlordHasBankAccounts" class="mt-5 text-center">
        <img src="@/assets/empty-bank-account.svg" width="210" />

        <h6 class="mt-5">
          {{ translationRegisterBankAccountOnProfile }}
        </h6>
        <router-link :to="{ name: 'landlord_profile_default_settings', params: { landlordId: this.landlordId } }">
          <hl-Button class="mt-3" variant="secondary" size="sm">{{ translationRegisterBankAccount }}</hl-Button>
        </router-link>
      </div>

      <!-- If tenancy has no bank account, but user can choose from a landlord bank account -->
      <div v-if="tenancyHasNoBankAccount && landlordHasBankAccounts" class="mt-5 text-center">
        <img src="@/assets/empty-bank-account.svg" width="210" />

        <h6 class="mt-5">{{ translationChooseBankRentPaymentNoBankAccount }}</h6>

        <hl-Button class="mt-3" variant="secondary" size="sm" @click="toggleEditTenancyBankAccountModal">
          {{ translationSelectBankAccount }}</hl-Button
        >
      </div>

      <!-- There is a tenancy bank account -->
      <List v-if="!tenancyHasNoBankAccount" class="mt-5">
        <div v-if="loading.updateBankAccount || !loaded.bankAccounts" class="text-center p-4">
          <hl-Loader class="m-auto"></hl-Loader>
        </div>

        <BankAccountCard v-else-if="currentBankAccount" :account="currentBankAccount"></BankAccountCard>
      </List>
    </BlockContainer>
  </div>
</template>

<script>
import BlockContainer from '@/components/BlockContainer.vue';
import BankAccountCard from '@/components/molecules/settings/BankAccountCard';
import EditButton from '@/components/UI/buttons/EditButton';
import List from '@/components/UI/list/List.vue';
import BankAccountSelect from '@modules/husleie-framework/src/components/molecules/forms/bank-account/BankAccountSelect.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import { mapActions, mapGetters } from 'vuex';

export default {
  components: {
    BankAccountCard,
    'hl-BankAccountSelect': BankAccountSelect,
    BlockContainer,
    EditButton,
    List,
  },
  data() {
    return {
      bankAccounts: [],
      currentBankAccount: null,
      localTenancyCopy: null,
      loaded: {
        bankAccounts: false,
      },
      loading: {
        updateBankAccount: false,
      },
      newBankAccount: null,
    };
  },
  mixins: [i18nCommon],
  props: {
    tenancyId: [String, Number],
    landlordId: [String, Number],
  },
  computed: {
    ...mapGetters(['isUserSudo']),
    ...mapGetters('tenancy', ['getTenancy', 'getTenancyCurrentBankAccount', 'isEditTenancyBankAccountModalShown']),

    tenancyHasNoBankAccount() {
      // Note getTenancyCurrentBankAccount is using async/await, tenancyHasNoBankAccount will not be valued correctly
      // until that method returns - added in check v-else-if="currentBankAccount" as temporary fix
      const bankAccount = this.getTenancyCurrentBankAccount;
      return bankAccount == null || bankAccount === '';
    },
    landlordHasBankAccounts() {
      return this.loaded.bankAccounts && this.bankAccounts.length > 0;
    },
    shouldShowEditButton() {
      return this.landlordHasBankAccounts && this.getTenancy.subscription.subscriptionPlanName !== 'Garantert';
    },
    translationCancel() {
      return this.translate('cancel');
    },
    translationUse() {
      return this.translate('use');
    },
    translationErrorCouldNotRetrieveBankAccount() {
      return this.translate('error.could not retrieve bank account');
    },
    translationErrorCouldNotSaveRent() {
      return this.translate('error.could not save rent details');
    },
    translationErrorCouldNotSaveTenacy() {
      return this.translate('error.could not save tenancy');
    },
    translationErrorCouldNotFetchTenacyDetails() {
      return this.translate('error.could not fetch tenancy details');
    },
    translationSelectBankAccount() {
      return this.translate('select bank account');
    },
    translationBankAccount() {
      return this.translate('bank account');
    },
    translationFoo() {
      return this.translate('tenancy.bank');
    },
    translationChooseBankRentPayment() {
      return this.translate('tenancy.bank.choose a bank for the payment of the rent');
    },
    translationChooseBankRentPaymentNoBankAccount() {
      return this.translate('tenancy.bank.please choose a bank for the payment of the rent');
    },
    translationRetrieveAccountNumber() {
      return this.translate('tenancy.bank.create a new account number here');
    },
    translationRegisterBankAccountOnProfile() {
      return this.translate('tenancy.bank.please register a bank account on profile');
    },
    translationRegisterBankAccount() {
      return this.translate('tenancy.bank.register a bank account');
    },
  },
  watch: {
    getTenancy() {
      this.generateCurrentBankAccount();
    },
  },
  methods: {
    ...mapActions('tenancy', [
      'updateTenancy',
      'updateTenancyRent',
      'fetchTenancy',
      'toggleEditTenancyBankAccountModal',
      'getBankInformationByAccountPrefix',
    ]),
    ...mapActions(['loadLandlordProfileBankAccounts']),
    setNewBankAccount(bankAccount) {
      this.newBankAccount = bankAccount;
    },
    changeCurrentBankAccount() {
      if (this.newBankAccount) {
        this.updateBankAccount();
        this.toggleEditTenancyBankAccountModal();
      } else {
        console.error('Trying to set an undefined bank account.');
      }
    },
    async updateBankAccount() {
      this.loading.updateBankAccount = true;
      try {
        const rent = JSON.parse(JSON.stringify(this.getTenancy.rent));
        rent.bankAccount = this.newBankAccount.bankAccount;
        await this.updateTenancyRent({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          data: rent,
        });
      } catch (error) {
        console.error(error);
        this.$toast(this.translationErrorCouldNotSaveRent, 'error');
      }
      this.loading.updateBankAccount = false;
    },
    async saveAndCloseEdit() {
      await this.saveTenancyDetails();
      this.customerInfoEditMode = false;
    },
    async saveTenancyDetails() {
      this.loading.post = true;
      const tenancy = JSON.parse(JSON.stringify(this.localTenancyCopy));

      // delete readOnly keys from tenancy Object
      delete tenancy.id;
      delete tenancy.rentalObjectId;
      delete tenancy.hasDeposits;
      delete tenancy.rent;
      delete tenancy.tenants;
      delete tenancy.subProducts;
      delete tenancy.rentAdjustment;
      delete tenancy.subscription;
      try {
        await this.updateTenancy({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          data: tenancy,
        });
        this.loading.post = false;
      } catch (error) {
        console.error(error);
        this.$toast(this.translationErrorCouldNotSaveTenacy, 'error');
      }
    },
    async generateCurrentBankAccount() {
      if (this.tenancyHasNoBankAccount) return;
      const bankAccount = this.getTenancyCurrentBankAccount;
      const prefix = bankAccount.substring(0, 4);
      try {
        const bank = await this.getBankInformationByAccountPrefix(prefix);
        this.currentBankAccount = { bank, bankAccount };
      } catch (error) {
        console.log('error ', error);
        this.$toast(this.translationErrorCouldNotRetrieveBankAccount, 'error');
      }
    },
  },
  async mounted() {
    try {
      const res = await this.loadLandlordProfileBankAccounts();
      this.bankAccounts = res;
      this.loaded.bankAccounts = true;
    } catch (error) {
      console.error(error);
      this.$toast(this.translationErrorCouldNotRetrieveBankAccount, 'error');
    }
  },
};
</script>

<style lang="scss" scoped>
.sudo {
  border: 2px solid #ed2135;
}
</style>
